import { TMessage } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { ReactNode, useRef } from "react";
import {
  Autocomplete2,
  Autocomplete2InputProps,
  Autocomplete2RenderItemFn,
  Autocomplete2SearchTextFn,
} from "./Autocomplete2";
import { IconButton } from "./Button";

export type SpotlightDialogProps<T> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string | TMessage;
  items: T[];
  itemTextFn: (item: T) => string;
  itemSearchTextFn?: Autocomplete2SearchTextFn<T>;
  renderItemFn: Autocomplete2RenderItemFn<T>;
  onSelect: (item: T) => void;
  inputProps?: Autocomplete2InputProps;
  header?: ReactNode;
  emptyMessage?: ReactNode;
};

const CloseButton = () => {
  const { t } = useTranslation();
  return (
    <div className="sm:hidden mr-2">
      <BaseDialog.Close
        render={(closeProps) => (
          <IconButton
            {...closeProps}
            icon={{ name: "x", weight: "bold" }}
            tooltip={t("close")}
            color={"inherit"}
            compact
          />
        )}
      />
    </div>
  );
};

export function SpotlightDialog<T>({
  open,
  onOpenChange,
  title,
  onSelect,
  inputProps,
  ...autocompleteProps
}: SpotlightDialogProps<T>) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 backdrop" />
        <div className="fixed inset-0 flex justify-center items-start sm:p-4 sm:pt-[15vh] pointer-events-none">
          <BaseDialog.Popup
            initialFocus={inputRef}
            finalFocus={false}
            className={classNames(
              "pointer-events-auto",
              "flex-1",
              "flex",
              "flex-col",
              "gap-2",
              "sm:max-w-3xl",
              "h-full",
              "sm:h-auto",
              "sm:max-h-[70vh]",
              "sm:rounded-sm",
              "bg-default",
              "shadow-2xl",
              "overflow-hidden",
            )}
          >
            <BaseDialog.Title className="sr-only">
              {typeof title === "string" ? title : t(title)}
            </BaseDialog.Title>
            <Autocomplete2<T>
              {...autocompleteProps}
              inputProps={{
                ...inputProps,
                addon: inputProps?.addon ?? <CloseButton />,
                className: "p-sm",
              }}
              inputRef={inputRef}
              onSelect={(item) => {
                onOpenChange(false);
                onSelect(item);
              }}
              inline
            />
          </BaseDialog.Popup>
        </div>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
